import { SLOT_HEADER, getActiveSlot } from './activeSlot';
import { API_TIMEOUT_MS, withTimeoutSignal } from './timeout';
import { nowMs, requestIdOf, traceApi } from '../diagnostics/trace';

/**
 * `fetch` authentifié (multi-login) : joint le cookie de session (same-origin) ET
 * l'en-tête `X-Active-Slot` désignant le compte actif. À utiliser pour TOUTE requête
 * `/api/*` authentifiée. Les requêtes publiques (`/api/health`) et browser-natives
 * (téléchargement) n'en ont pas besoin — ces dernières passent par `withSlotParam`
 * (cf. `activeSlot.ts`).
 *
 * Chaque appel est TRACÉ dans les anneaux de diagnostic (métadonnées HTTP :
 * méthode/chemin assaini/statut/durée/`x-request-id`), pour l'enveloppe « Signaler
 * un bug ». Aucun corps, aucune query-string.
 *
 * Chaque appel est aussi BORNÉ dans le temps (`timeoutMs`, défaut `API_TIMEOUT_MS`) :
 * un serveur muet à socket ouverte ferait sinon attendre l'UI indéfiniment, sans
 * exception ni message. Le `signal` éventuel de l'appelant (annulation au démontage)
 * est COMPOSÉ avec le garde-temps, jamais remplacé.
 */
export async function apiFetch(
  input: string,
  init: RequestInit = {},
  timeoutMs: number = API_TIMEOUT_MS,
): Promise<Response> {
  const headers = new Headers(init.headers);
  const slot = getActiveSlot();
  if (slot !== null) headers.set(SLOT_HEADER, String(slot));
  const method = (init.method ?? 'GET').toUpperCase();
  const signal = withTimeoutSignal(init.signal, timeoutMs);
  const started = nowMs();
  try {
    // `apiFetch` ne sert QUE des données (liste/détail/admin/auth). On marque ces
    // requêtes « high » (priorité de flux HTTP/2) ; un appelant peut surcharger via
    // `init.priority`.
    const res = await fetch(input, {
      credentials: 'same-origin',
      priority: 'high',
      ...init,
      headers,
      ...(signal ? { signal } : {}),
    });
    traceApi(method, input, res.status, Math.round(nowMs() - started), requestIdOf(res));
    return res;
  } catch (err) {
    // Échec réseau (pas de réponse HTTP) : trace en statut 0 pour le distinguer.
    traceApi(method, input, 0, Math.round(nowMs() - started), null);
    throw err;
  }
}
